import { PAGINATION_DEFAULTS, TOAST_VARIANT } from "@root/constants";
import { AdminFileManagement } from "@root/services/admin/adminFileManagement";
import { selectDrawerUser } from "@root/store/selectors/admin/app";
import { selectAdminUsers } from "@root/store/selectors/admin/usersManagement";
import { getBaseUrl } from "@root/store/selectors/features";
import { deActivateUser } from "@root/store/thunks/admin/dashboard";
import {
  fetchAdminUserByID,
  fetchAdminUsers,
  addUser,
} from "@root/store/thunks/admin/userManagement";
import { querybuilder } from "@root/utils/helpers";
import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import { useNotification } from "./useNotification";
import { set } from "react-datepicker/dist/date_utils";

function useAdminUsers() {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const { showNotification } = useNotification();

  const userManagementHeader = [
    t("admin.userId"),
    t("admin.name"),
    t("admin.Email"),
    t("admin.lastLogin"),
    t("admin.status"),
    t("admin.actions"),
  ];
  const adminUsers = useSelector(selectAdminUsers);
  const drawerUser: any = useSelector(selectDrawerUser);
  const [deactivatePopup, setdeactivatePopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [folders, setFolders] = useState(null);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [files, setFiles] = useState(null);
  const [uploadFileLoading, setUploadFileLoading] = useState(false);
  const [projects, setProjects] = useState(null);
  const [ProjectsLoading, setProjectsLoading] = useState(false);
  const [filesLoading, setFilesLoading] = useState(false);
  const [createFolderLoading, setCreateFolderLoading] = useState(false);
  const [lastLoginDates, setLastLoginDates] = useState("");
  const [userDetailsDrawer, setUserDetailsDrawer] = useState(false);
  const [folderCount, setFolderCount] = useState(0);
  const [folderPage, setFolderPage] = useState(1);
  const [fileCount, setFileCount] = useState(0);
  const [filePage, setFilePage] = useState(1);
  const [fileDeleteLoading, setFileDeleteLoading] = useState(false);
  const [folderDeleteLoading, setFolderDeleteLoading] = useState(false);
  const [deactivationLoading, setDeactivationLoading] = useState(false);
  const [count, setCount] = useState(null);

  const USER_STATUSS = {
    ALL: t("userStatus.all"),
    ACTIVATED: t("userStatus.activated"),
    DEACTIVATED: t("userStatus.deactivated"),
  };
  const userStatuses: string[] = [
    //USER_STATUSS.ALL,
    USER_STATUSS.ACTIVATED,
    USER_STATUSS.DEACTIVATED,
  ];
  const adminFileMangement = new AdminFileManagement();
  // @ts-ignore
  const [currentStatus, setCurrentStatus] = useState("");

  const getFolders = async (payload: any) => {
    try {
      setFoldersLoading(true);
      const baseAuthUrl = process.env.REACT_APP_BASE_URL || "";
      const result = await adminFileMangement.fetchFolders(
        baseAuthUrl,
        payload
      );
      setFolders(result?.data?.data?.rows);
      setFolderCount(result?.data?.data?.count);
    } catch (error) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("projects.fetchFoldersFailed") || "Failed to fetch folders",
      });
    } finally {
      setFoldersLoading(false);
    }
  };

  const createFolder = async (payload: any) => {
    try {
      const { projectId } = payload;
      setCreateFolderLoading(true);
      const baseAuthUrl = process.env.REACT_APP_BASE_URL || "";
      const result = await adminFileMangement.createFolder(
        baseAuthUrl,
        payload
      );
      showNotification({
        type: TOAST_VARIANT.SUCCESS,
        message: t("projects.folderCreated"),
      });
      getFolders({ projectId: projectId });
    } catch (error) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message:
          t("projects.folderAddFailedError") || "Failed to create folder",
      });
    } finally {
      setCreateFolderLoading(false);
    }
  };

  const getFiles = async (payload: any, skipLoading: boolean = false) => {
    try {
      if (!skipLoading) {
        setFilesLoading(true);
      }
      const baseAuthUrl = process.env.REACT_APP_BASE_URL || "";
      const result = await adminFileMangement.fetchFiles(baseAuthUrl, payload);
      setFiles(result?.data?.data?.rows);
      setFileCount(result?.data?.data?.count);
      if (!skipLoading) {
        setFilesLoading(false);
      }
    } catch (error) {
    } finally {
      if (!skipLoading) {
        setFilesLoading(false);
      }
    }
  };

  const DeleteFile = async (payload: any) => {
    try {
      setFileDeleteLoading(true);
      const baseAuthUrl = process.env.REACT_APP_BASE_URL || "";
      const result = await adminFileMangement.DeleteFile(baseAuthUrl, payload);

      showNotification({
        type: TOAST_VARIANT.SUCCESS,
        message: t("projects.fileDeleted"), // success message
      });

      console.log(result);
    } catch (error) {
      // Show error notification
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("projects.fileDeleteFailed") || "Failed to delete file",
      });
      console.error("Failed to delete file:", error);
    } finally {
      setFileDeleteLoading(false);
      setFilesLoading(false);
    }
  };

  //delete folder
  const DeleteFolder = async (payload: any) => {
    try {
      setFolderDeleteLoading(true);
      const baseAuthUrl = process.env.REACT_APP_BASE_URL || "";
      const result = await adminFileMangement.DeleteFolder(
        baseAuthUrl,
        payload
      );
      showNotification({
        type: TOAST_VARIANT.SUCCESS,
        message: t("projects.folderDeleted"),
      });
      console.log(result);
    } catch (error) {
      // Show error notification
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("projects.folderDeleteFailed") || "Failed to delete folder",
      });
      throw error; // Re-throw to allow caller to handle
    } finally {
      setFolderDeleteLoading(false);
    }
  };

  const getProjects = async (payload: any) => {
    try {
      setProjectsLoading(true);
      const baseAuthUrl = process.env.REACT_APP_BASE_URL || "";
      const result = await adminFileMangement.fetchProjects(
        baseAuthUrl,
        payload
      );
      setProjects(result?.data?.data?.rows);
    } catch (error) {
    } finally {
      setProjectsLoading(false);
    }
  };

  const uploadFile = async (payload: any) => {
    try {
      setUploadFileLoading(true);
      const baseAuthUrl = process.env.REACT_APP_BASE_URL || "";
      const result = await adminFileMangement.uploadFile(baseAuthUrl, payload);
    } catch (error) {
    } finally {
      setUploadFileLoading(false);
    }
  };

  const retryFile = async (payload: {
    project_id: number;
    fileName_key: string;
    fileName: string;
    id: number;
  }) => {
    try {
      setFileDeleteLoading(true); // Reuse loading state for retry
      const baseAuthUrl = process.env.REACT_APP_BASE_URL || "";
      const result = await adminFileMangement.retryDocument(
        baseAuthUrl,
        payload
      );
      showNotification({
        type: TOAST_VARIANT.SUCCESS,
        message: t("projects.retrySuccess"),
      });
      return result;
    } catch (error) {
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: t("projects.retryFailed"),
      });
      throw error;
    } finally {
      setFileDeleteLoading(false);
    }
  };

  // UTILITY FUNCTION
  const fetchUsers = (data: IUserMangementGetFields) => {
    setLoading(true);
    const payload = {
      onSuccess: (count: any) => {
        setLoading(false);

        // if response contains count, set it
        if (count !== undefined) {
          setCount(count);
        }
      },
      onError: () => setLoading(false),
      payload: data,
    };
    dispatch(fetchAdminUsers(payload));
  };

  const addUsers = (data: any) => {
    setLoading(true);

    const payload = {
      onSuccess: () => {
        setLoading(false);

        showNotification({
          type: TOAST_VARIANT.SUCCESS,
          message: t("userManagement.userCreated"),
        });

        // set page 1 and Refetch admin users list after add
        setPage(1);
        fetchUsers({
          page: 1,
          searchQuery: search,
          limit: PAGINATION_DEFAULTS.PAGESIZE,
          ...(currentStatus === USER_STATUSS.ALL
            ? {}
            : {
                status:
                  currentStatus === USER_STATUSS.ACTIVATED
                    ? true
                    : currentStatus === USER_STATUSS.DEACTIVATED
                    ? false
                    : undefined,
              }),
          ...(!lastLoginDates?.[0] && !lastLoginDates?.[1]
            ? {}
            : {
                start_date: lastLoginDates?.[0],
                end_date: lastLoginDates?.[1],
              }),
        });
      },
      onError: () => setLoading(false),
      payload: data,
    };

    dispatch(addUser(payload));
  };

  //INITIAL DATA
  useEffect(() => {
    fetchUsers({
      page: page,
      searchQuery: search,
      limit: PAGINATION_DEFAULTS.PAGESIZE,
    });
  }, []);

  const onPageChange = (page: number) => {
    fetchUsers({
      page,
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      ...(currentStatus === USER_STATUSS.ALL
        ? {}
        : {
            status:
              currentStatus === USER_STATUSS.ACTIVATED
                ? true
                : currentStatus === USER_STATUSS.DEACTIVATED
                ? false
                : undefined,
          }),
      ...(!lastLoginDates?.[0] && !lastLoginDates?.[1]
        ? {}
        : { start_date: lastLoginDates?.[0], end_date: lastLoginDates?.[1] }),
      searchQuery: search,
    });
  };
  // HANDLE INPUT CHANGE FILTER
  const debounced = useDebouncedCallback(() => {
    setPage(1);
    onPageChange(1);
  }, 1000);

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event?.target?.value);
    debounced();
  };

  // HANDLE DATE CHANGE FILTER
  const onDateChange = (date: any) => {
    setSearch("");
    setLastLoginDates(date);
    if (date) {
      const [start_date, end_date] = date;
      if (start_date && end_date) {
        fetchUsers({
          page: page,
          limit: PAGINATION_DEFAULTS.PAGESIZE,
          start_date,
          end_date,
          ...(currentStatus === USER_STATUSS.ALL
            ? {}
            : {
                status:
                  currentStatus === USER_STATUSS.ACTIVATED
                    ? true
                    : currentStatus === USER_STATUSS.DEACTIVATED
                    ? false
                    : undefined,
              }),
        });
      }
    }
    if (date === null) {
      setPage(1);
      fetchUsers({
        page: page,
        searchQuery: search,
        limit: PAGINATION_DEFAULTS.PAGESIZE,
      });
    }
  };

  // HANDLE STATUS CHANGE FILTER
  const onStatusChange = (status: string) => {
    setSearch("");
    setPage(1);
    setCurrentStatus(status);
    fetchUsers({
      page: 1,
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      ...(status === USER_STATUSS.ALL
        ? {}
        : { status: status === USER_STATUSS.ACTIVATED ? true : false }),
      ...(!lastLoginDates?.[0] && !lastLoginDates?.[1]
        ? {}
        : { start_date: lastLoginDates?.[0], end_date: lastLoginDates?.[1] }),
      searchQuery: search,
    });
  };

  // CLICK ON SEE DETAILS
  const onViewDetails = (item: any) => {
    const onSuccess = () => {
      setUserDetailsDrawer(true);
    };
    const onError = () => {};
    if (item?.id) {
      const data = {
        payload: {
          userId: item?.id,
        },
        onSuccess,
        onError,
      };
      dispatch(fetchAdminUserByID(data));
    }
  };

  // HANDLE DEACTIVATION
  const onDeactivateUser = () => {
    if (drawerUser?.id) {
      setDeactivationLoading(true);
      dispatch(
        deActivateUser({
          payload: {
            userId: drawerUser?.id,
            reactivate: !drawerUser?.is_active,
          },
          onSuccess: () => {
            setdeactivatePopup(false);
            setDeactivationLoading(false);
            setUserDetailsDrawer(false);
            fetchUsers({
              page: page,
              searchQuery: search,
              limit: PAGINATION_DEFAULTS.PAGESIZE,
            });
          },
          onError: () => {
            setDeactivationLoading(false);
          },
        })
      );
    }
  };

  return {
    loading,
    adminUsers,
    addUsers,
    onPageChange,
    page,
    setPage,
    onInputChange,
    onDateChange,
    onStatusChange,
    currentStatus,
    userStatuses,
    search,
    createFolderLoading,
    onViewDetails,
    getFolders,
    foldersLoading,
    folders,
    count,
    createFolder,
    userDetailsDrawer,
    setUserDetailsDrawer,
    onDeactivateUser,
    deactivatePopup,
    setdeactivatePopup,
    deactivationLoading,
    lastLoginDates,
    drawerUser,
    fileCount,
    setFilePage,
    filePage,
    fetchUsers,
    DeleteFolder,
    getFiles,
    files,
    filesLoading,
    userManagementHeader,
    uploadFile,
    getProjects,
    ProjectsLoading,
    projects,
    folderCount,
    folderPage,
    setFolderPage,
    DeleteFile,
    fileDeleteLoading,
    folderDeleteLoading,
    retryFile,
  };
}

export default useAdminUsers;
